<template>
  <div v-if="instructor" class="instructors-page">
    <div
      class="hero-section"
      :style="{ backgroundImage: `url(${getLargeImage(instructor.largeImage)})` }"
    >
      <div class="container">
        <div class="row">
          <div class="instructor-info col col-4" :class="{ mt: $config.project === 'site' }">
            <h1 class="h2">
              {{ instructor.name }}
            </h1>
            <p class="h5 instructor-descr">
              {{ instructor.instructorShortDescription }}
            </p>
            <div class="instructor-signature">
              <span>{{ instructor.signature }}</span>
              <Component :is="signatureCompany(instructor.companies.find(c => +c.id === +instructor.signatureCompany).slug)" v-if="instructor.companies.find(c => +c.id === +instructor.signatureCompany)" class="signature-company" />
            </div>
          </div>
        </div>
      </div>
    </div>
    <Lazy never>
      <div class="container content">
        <div class="about row">
          <div class="col" :class="{ 'col-7 md-6': instructor.linkedin, 'col-12': !instructor.linkedin }">
            <h2>About the Instructor</h2>
            <p class="body-2">
              {{ instructor.instructorLongDescription }}
            </p>
          </div>
          <div
            v-if="instructor.linkedin"
            class="col col-4 offset-1 md-5"
          >
            <div
              class="badge-base LI-profile-badge"
              data-locale="en_US" data-size="large" data-theme="light"
              :data-type="$store.state.layout === 'mobile' ? 'VERTICAL' : 'HORIZONTAL'"
              :data-vanity="instructor.linkedinShort" data-version="v1"
            />
          </div>
        </div>
        <div class="skills">
          <div class="row">
            <div class="col col-6">
              <img loading="lazy" :src="`${$config.imagesUrl}/team/${instructor.smallImage}`" :alt="instructor.name">
            </div>
            <div class="col col-6">
              <h3>Skills and Specialization</h3>
              <p class="body-2">
                {{ instructor.specialization }}
              </p>
              <div class="boxes">
                <span v-for="(skill, i) in instructor.skillBoxes" :key="i" class="skill-box caption">{{ skill }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Lazy>
    <div v-if="instructor.courses && instructor.courses.length" class="courses">
      <div class="container">
        <div class="row">
          <div class="col col-4">
            <h3>Courses with {{ instructor.name }}</h3>
            <ButtonArrow text="Start Your Career" type="nuxt-link" to="/signup/" />
          </div>
          <div class="col col-8">
            <div class="row">
              <Slider
                v-if="instructor.courses && instructor.courses.length"
                :disabled="instructor.courses.length <= 2"
                disable-swipes
                :class="{ container: $store.state.layout === 'desktop' }"
              >
                <CourseCard
                  v-for="course in instructor.courses"
                  :key="course.id"
                  slot="slide"
                  class="col col-6 sm-6"
                  :course="{ ...course, type: 'similar' }"
                />
              </Slider>
            </div>
          </div>
        </div>
      </div>
    </div>
    <Lazy :when-visible="{ rootMargin: '200px' }">
      <div v-if="instructor.trainingSlider && instructor.trainingSlider.length" class="live-trainings">
        <div class="container">
          <Slider :disabled="instructor.trainingSlider.length <= 3" mobile-no-swipe>
            <h3 slot="heading">
              Live Trainings with {{ instructor.name.split(' ')[0] }}
            </h3>
            <TrainingCard
              v-for="(training, id) in instructor.trainingSlider"
              slot="slide" :key="id"
              :training="training" :to="`/live-trainings/${training.slug}/`" hide-description
            />
          </Slider>
        </div>
      </div>
    </Lazy>
  </div>
</template>

<script>
import Lazy from 'vue-lazy-hydration';
import global from '@itbpbg/lms-components/src/mixins/global';
import ButtonArrow from '@itbpbg/lms-components/src/components/ButtonArrow';
import Slider from '@itbpbg/lms-components/src/components/Slider';
import CourseCard from '@itbpbg/lms-components/src/Courses/Card';
import TrainingCard from '@itbpbg/lms-components/src/Business/LiveTrainings/Card';

/* eslint-disable global-require */
/* eslint-disable import/no-dynamic-require */
export default {
  components: {
    Lazy, ButtonArrow, Slider, CourseCard, TrainingCard,
  },
  mixins: [global],
  async asyncData({
    $axios, route, $config, error,
  }) {
    if ($config.platform === 'fa') {
      return error({ statusCode: 404, message: 'Page not found' });
    }
    const instructor = await $axios.$get(
      `/team/all/${route.params.instructor}`,
    ).catch(() => false);

    if (!instructor) {
      return error({ title: 'Not found', statusCode: 404, message: 'Instructor not found' });
    }
    instructor.fullPath = `${$config.siteUrl}${route.fullPath}`;

    return {
      instructor,
    };
  },
  head() {
    const metaTitle = `${this.instructor.name} – Instructor`;
    const metaDescription = `Meet our instructor ${this.instructor.name}. Learn more about their education, experience, and courses at 365 Data Science. Start now.`;
    const linkedData = this.$linkedDataLoad([
      { key: 'WebSite' },
      { key: 'WebPage', values: { ...this.page, metaTitle, metaDescription } },
      {
        key: 'Person',
        values: {
          name: this.instructor.name,
          url: `${this.$config.siteUrl}${this.$route.fullPath}`,
          image: {
            '@type': 'ImageObject',
            url: `${this.$config.imagesUrl}/team/${this.instructor.image}`,
            caption: `${this.instructor.name}'s image`,
          },
        },
      },
    ]);

    return {
      titleTemplate: `${metaTitle}%s`,
      link: [
        { rel: 'canonical', href: this.canonicalURL() },
      ],
      meta: this.metaProps({
        ...this.instructor,
        metaTitle,
        metaDescription,
      }),
      script: [
        linkedData,
        ...[(this.instructor.linkedin ? { src: 'https://platform.linkedin.com/badges/js/profile.js', async: true } : {})],
      ],
    };
  },
  beforeDestroy() {
    clearInterval(this.stylesInterval);
  },
  methods: {
    getLargeImage(img) {
      return `${this.$config.imagesUrl}/team/${img}` || '';
    },
    signatureCompany(slug) {
      let company = '';
      if (slug) {
        company = require(`../../assets/svg/signatures/company-${slug}.svg?vue-component`);
      }

      return company;
    },
  },
};
</script>

<style lang="scss">
.instructors-page {
  padding-bottom: 100px;

  .hero-section {
    height: 650px;
    position: relative;
    background-position: center;
    background-repeat: no-repeat;
    background-size: cover;
    image-rendering: -webkit-optimize-contrast;

    .container {
      position: relative;
      height: 100%;
      display: flex;
      justify-content: space-between;
      align-items: center;
      z-index: 2;
      color: var(--main-light);

      .instructor-descr {
        font-weight: normal;
        line-height: 140%;
        color: var(--main-light-shade-4);
        margin: var(--spacing-16) 0 var(--spacing-36);
      }

      .instructor-signature {
        display: flex;
        align-items: center;

        svg {
          width: 77px;
          margin-left: 14px;

          path[fill="#ABAFB5"] {
            fill: var(--main-light);
          }
        }
      }
    }

    @include bp($bp-mobile) {
      background-position: 65%;
      position: relative;

      &:after {
        content: '';
        position: absolute;
        top: 0;
        height: 100%;
        width: 50%;
        background: linear-gradient(90deg, rgba(0, 12, 31, 0.94) 0%, var(--main-dark) 0.01%, rgba(0, 12, 31, 0.98) 56.77%, rgba(0, 12, 31, 0) 100%);
      }

      .container .instructor-info {
        max-width: 40%;

        .h2 {
          font-size: 32px;
        }
      }
    }

    @include bp($bp-tablet) {
      .bg-image {
        display: none;
      }
    }

    @include bp($bp-phone) {
      height: auto;
      overflow: hidden;
      background-repeat: no-repeat;
      background-size: auto 500px;
      background-position: 75% 0;
      background-color: var(--main-dark);

      .container {
        padding: var(--spacing-48) var(--spacing-24);
        padding-top: 500px;

        .instructor-info {
          max-width: 100%;

          h1 {
            font-size: 28px;
          }

          .instructor-descr {
            font-size: 16px;
          }
        }
      }

      &:after {
        content: '';
        position: absolute;
        top: unset;
        bottom: 0;
        width: 100%;
        height: 500px;
        background: linear-gradient(0deg, var(--main-dark) 70%, rgba(0, 12, 31, 0.96) 70.77%, rgba(0, 12, 31, 0) 100%);
      }

      &:before {
        content: '';
        position: absolute;
        top: -210px;
        width: 100%;
        height: 360px;
        background: linear-gradient(180deg, var(--main-dark) 0%, rgba(0, 12, 31, 0.96) 70.77%, rgba(0, 12, 31, 0) 100%);
        z-index: 1;
      }
    }
  }

  .content {
    margin-top: var(--spacing-120);
    margin-bottom: var(--spacing-160);

    .about {
      iframe {
        width: 100%;
      }
    }

    .skills {
      margin: var(--spacing-160) 0;

      .body-2 {
        margin: var(--spacing-16) 0 var(--spacing-48);
      }

      .skill-box {
        background: var(--main-dark-shade-1);
        border-radius: 100px;
        padding: var(--spacing-8) var(--spacing-16);
        font-weight: 500;
        text-align: center;
        color: var(--main-light);
        margin: 0 var(--spacing-14) var(--spacing-12) 0;
        display: inline-block;
        text-transform: capitalize;
      }

      img {
        border-radius: 10px;
        max-width: 100%;
      }
    }

    @include bp($bp-mobile) {
      margin-top: var(--spacing-80);
      margin-bottom: var(--spacing-80);

      .about {
        h2 {
          font-size: 32px;
        }

        iframe {
          margin: var(--spacing-40) -8px 0;
          width: calc(100% + 16px);
          padding: 0;
        }
      }

      .skills {
        margin-top: var(--spacing-80);
        margin-bottom: var(--spacing-80);

        img {
          width: auto;
          margin-bottom: var(--spacing-40);
        }
      }
    }

    @include bp($bp-phone) {
      .skills img {
        width: 100%;
      }
    }
  }

  .courses {
    h3 {
      margin-bottom: var(--spacing-36);
    }

    @include bp($bp-mobile) {
      .slider-container {
        padding: 20px 24px;
        margin-top: -20px;
        margin-bottom: -20px;

        .slider-header {
          display: none;
        }

        .row.swiper {
          flex-wrap: wrap;
        }

        .dots {
          display: none;
        }
      }

      .btn-arrow {
        margin-bottom: var(--spacing-48);
      }
    }
  }

  .live-trainings {
    background: var(--main-light-shade-1);
    padding: var(--spacing-110) 0;

    .training-card {
      .image-holder {
        height: 250px;
        width: 100%;
      }
    }

    @include bp($bp-mobile) {
      padding-bottom: var(--spacing-80);

      .training-card {
        margin-bottom: 31px;

        &:last-of-type {
          margin-bottom: 0;
        }

        .image-holder {
          height: 221px;
        }
      }

      .slider-container {
        .row {
          flex-wrap: wrap;
        }
      }
    }
  }

  @include bp($bp-mobile) {
    padding-bottom: 80px;

    h3 {
      font-size: 32px;
    }
  }
}
</style>
